import { nanoid } from 'nanoid';
import type { ReadonlyDeep } from 'type-fest';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { TrendingSectionData } from '#src/app/pages/homepage/shared/api';
import { getTrendingSectionData } from '#src/app/pages/homepage/shared/api';
import { getMarqueeCardImageUrl } from '#src/app/pages/homepage/shared/utils';

import { HomepageData, type HomepageDataQuery, type HomepageDataQueryVariables } from './api.graphql';

export type HomepagePageData = ReadonlyDeep<{
	totalUserCount: HomepageDataQuery['totalUserCount'];
	topImpactStories: HomepageDataQuery['topImpactStories'];
	petitionMarqueeItems: HomepageDataQuery['petitionMarqueeItems'];
	/**
	 * The middle-end returns an empty list when the homepage_petition_marquee FCM is disabled,
	 * so a non-empty list stands in for the FCM's isEnabled flag. Items with no usable image
	 * (see getMarqueeCardImageUrl) are already filtered out, so a true here guarantees
	 * visible cards - otherwise the hero could pick the marquee over its impact-stories
	 * fallback and render an empty slot.
	 */
	shouldShowPetitionMarquee: boolean;
	aiSapId: string;
	/**
	 * Only fetched for bots. Humans load the trending section lazily on scroll (it's below
	 * the fold), but crawlers that don't run JS would then never see its petition and topic
	 * links, so for them the data is fetched up front and the section is server-rendered.
	 * The bot flag is part of the SSR cache key, so the two variants are cached separately.
	 */
	trendingSectionData: TrendingSectionData | undefined;
}>;

type Params = { locale: string; countryCode: string; isBot: boolean };

/**
 * The trending section is non-critical (it renders nothing on error), so a failure here
 * must not take the whole homepage down for bots - degrade to the lazy path instead.
 * gql.fetch has already reported the failure, so it isn't reported again here.
 */
async function getTrendingSectionDataForBots(
	utilityContext: UtilityContext,
	{ locale, countryCode, isBot }: Params,
): Promise<TrendingSectionData | undefined> {
	if (!isBot) return undefined;
	try {
		return await getTrendingSectionData(utilityContext, { locale, countryCode });
	} catch {
		return undefined;
	}
}

export async function getHomepagePageData(utilityContext: UtilityContext, params: Params): Promise<HomepagePageData> {
	const {
		gql: { fetch },
	} = utilityContext;
	const [{ data }, trendingSectionData] = await Promise.all([
		fetch<HomepageDataQuery, HomepageDataQueryVariables>({
			query: HomepageData,
			variables: {},
			rejectOnError: true,
		}),
		getTrendingSectionDataForBots(utilityContext, params),
	]);

	// the same image check usePetitionMarquee applies when it renders the cards, so every
	// entry kept here produces a visible card
	const petitionMarqueeItems = data.petitionMarqueeItems.filter((item) => !!getMarqueeCardImageUrl(item));

	return {
		totalUserCount: data.totalUserCount,
		topImpactStories: data.topImpactStories,
		petitionMarqueeItems,
		shouldShowPetitionMarquee: petitionMarqueeItems.length > 0,
		aiSapId: nanoid(),
		trendingSectionData,
	};
}
